import { inject } from "../../di";
import type { IWritableExploreCreatorsState, IExploreCreatorsService, IExploreCreatorsData } from "./types";
import type { IRpcService } from "../rpc/types";

export class ExploreCreatorsService implements IExploreCreatorsService {
  @inject<IWritableExploreCreatorsState>("writableExploreCreatorsState")
  private state!: IWritableExploreCreatorsState;

  @inject<IRpcService>("rpcService")
  private rpcService!: IRpcService;

  private pendingRequest: Promise<void> | null = null;
  private currentParams: {
    limit: number;
    offset: number;
    personalize: boolean;
    search?: string;
    categoryIds?: string[];
  } | null = null;

  loadCreators(limit = 20, offset = 0, personalize = true, search?: string, categoryIds?: string[]): void {
    if (this.pendingRequest) return;

    this.currentParams = { limit, offset, personalize, search, categoryIds };
    this.state.loading.set(true);
    this.state.error.set(null);

    this.pendingRequest = this.doLoad(limit, offset, personalize, search, categoryIds);
  }

  private async doLoad(
    limit: number,
    offset: number,
    personalize: boolean,
    search?: string,
    categoryIds?: string[],
  ): Promise<void> {
    try {
      const result = await this.rpcService.call<IExploreCreatorsData>("get-creators", {
        limit,
        offset,
        personalize,
        search,
        categoryIds,
      });

      if (offset === 0) {
        this.state.creators.set(result);
      } else {
        const current = this.state.creators.get();
        if (current) {
          const existingIds = new Set(current.influencers.map((c) => c.id));
          const newInfluencers = result.influencers.filter((c) => !existingIds.has(c.id));
          this.state.creators.set({
            influencers: [...current.influencers, ...newInfluencers],
            totalCount: result.totalCount,
            hasMore: result.hasMore,
          });
        } else {
          this.state.creators.set(result);
        }
      }
    } catch (error) {
      console.error("[ExploreCreatorsService] Error loading creators:", error);
      this.state.error.set("Failed to load creators");
    } finally {
      this.state.loading.set(false);
      this.pendingRequest = null;
    }
  }

  loadMore(): void {
    if (!this.currentParams) return;
    const current = this.state.creators.get();
    if (!current?.hasMore) return;

    const newOffset = current.influencers.length;
    this.currentParams = { ...this.currentParams, offset: newOffset };
    this.pendingRequest = null;
    this.loadCreators(
      this.currentParams.limit,
      newOffset,
      this.currentParams.personalize,
      this.currentParams.search,
      this.currentParams.categoryIds,
    );
  }

  refetch(search?: string, categoryIds?: string[]): void {
    this.pendingRequest = null;
    const params = this.currentParams || { limit: 20, offset: 0, personalize: true };
    this.state.creators.set(null);
    this.loadCreators(params.limit, 0, params.personalize, search ?? params.search, categoryIds ?? params.categoryIds);
  }

  clearCache(): void {
    this.state.creators.set(null);
    this.state.loading.set(false);
    this.state.error.set(null);
    this.pendingRequest = null;
    this.currentParams = null;
  }
}
